import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export function FAQAccordion() {
  const [openSection, setOpenSection] = useState<string | null>('timelines');
  const [openItem, setOpenItem] = useState<string | null>('timeline-1');
  // Cleaned FAQ data – original corrupted lines are retained as comments for reference
  const faqData = [{
    id: 'timelines',
    title: 'Project Timelines',
    items: [{
      id: 'timeline-1',
      question: 'How long does it take to build a website?',
      answer: <>
              For standard websites, we typically deliver within 2-3 weeks from
              the date we receive all required content and approvals. E-commerce
              websites generally take 3-5 weeks depending on complexity. We
              provide you with a specific timeline during our initial
              consultation, based on your project requirements. View our{' '}
              <Link to="/pricing" className="text-[#3AA6F9] hover:underline" data-id="element-177">
                pricing packages
              </Link>{' '}
              for more details.
            </>
    }, {
      id: 'timeline-2',
      question: 'What is the process for building a website?',
      answer: 'Our process includes: 1) Discovery & planning, 2) Design approval, 3) Development, 4) Content integration, 5) Testing & refinement, 6) Launch, and 7) Post-launch support. We keep you involved at every stage to ensure the final product meets your expectations.'
    }, {
      id: 'timeline-3',
      question: 'Can you rush my project if I have a deadline?',
      answer: 'Yes, we offer expedited services for urgent projects, though this may incur an additional fee. Please let us know your deadline upfront, and we will do our best to accommodate your timeline without compromising quality.'
    }]
  }, {
    id: 'ownership',
    title: 'Ownership & Access',
    items: [{
      id: 'ownership-1',
      question: 'Who owns the website once it is completed?',
      answer: 'You do! Once the project is paid in full, you own 100% of your website. We never hold your site hostage or restrict access. We provide all necessary login credentials and can assist with transferring the site if needed in the future.'
    }, {
      id: 'ownership-2',
      question: 'Will I have admin access to my website?',
      answer: 'Absolutely. We provide full administrator access to your website and content management system. We also offer training on how to make basic updates yourself if you wish to do so.'
    }, {
      id: 'ownership-3',
      question: 'Can I move my website to another host later?',
      answer: 'Yes, you have complete freedom to move your website to any hosting provider. If you decide to move, we can provide all necessary files and databases, and we can even assist with the transfer for a small fee.'
    }]
  }, {
    id: 'hosting',
    title: 'Hosting & Domain',
    items: [{
      id: 'hosting-1',
      question: 'Do you provide hosting services?',
      answer: <>
              Yes, all our websites come with 3 months of free hosting. After
              that, you can choose one of our{' '}
              <Link to="/website-care" className="text-[#3AA6F9] hover:underline" data-id="element-178">
                Website Care Plans
              </Link>{' '}
              which include hosting, or we can help you set up hosting elsewhere
              if you prefer.
            </>
    }, {
      id: 'hosting-2',
      question: 'What is included with your hosting?',
      answer: <>
              Our hosting includes server space, SSL certificate, daily backups,
              security monitoring, and regular updates to keep your site secure
              and performing well. We also provide email accounts based on your
              chosen package. Learn more about our{' '}
              <Link to="/website-care" className="text-[#3AA6F9] hover:underline" data-id="element-179">
                care plans
              </Link>
              .
            </>
    }, {
      id: 'hosting-3',
      question: 'Can I use my existing domain name?',
      answer: "Absolutely! We can use your existing domain name. We guide you through pointing it to our servers or help you transfer it to our management if you prefer. If you don't have a domain yet, we can register one for you as part of our service."
    }]
  }, {
    id: 'content',
    title: 'Content & Design',
    items: [{
      id: 'content-1',
      question: 'Do you write content for websites?',
      answer: <>
              Yes, we offer content writing services for an additional fee. Our
              Silver and Bronze packages don't include content creation by
              default, but our Gold package includes basic content writing. We
              can also work with content you provide and optimize it for SEO.
              Check our{' '}
              <Link to="/pricing" className="text-[#3AA6F9] hover:underline" data-id="element-180">
                pricing page
              </Link>{' '}
              for package details.
            </>
    }, {
      id: 'content-2',
      question: 'Can I provide my own images?',
      answer: "Yes, you can provide your own images. If you don't have suitable images, we can source high-quality stock photos that match your brand and website design. For best results, we recommend professional photography."
    }, {
      id: 'content-3',
      question: 'How much input do I have in the design process?',
      answer: 'You have significant input in the design process. We start by understanding your preferences, brand guidelines, and competitors. We then create design mockups for your approval before proceeding with development. We offer up to two rounds of revisions to ensure you are completely satisfied.'
    }]
  }, {
    id: 'updates',
    title: 'Updates & Support',
    items: [{
      id: 'updates-1',
      question: 'What support do you provide after launch?',
      answer: <>
              All our packages include at least 30 days of post-launch support
              to address any issues that might arise. For ongoing support, we
              recommend our{' '}
              <Link to="/website-care" className="text-[#3AA6F9] hover:underline" data-id="element-181">
                Website Care Plans
              </Link>
              , which include regular updates, security monitoring, and
              technical support.
            </>
    }, {
      id: 'updates-2',
      question: 'Can I update the website myself?',
      answer: 'Yes, we build our websites with user-friendly content management systems that allow you to make basic updates yourself. We provide training on how to update text, images, and add new content. For more complex changes, our team is available to help.'
    }, {
      id: 'updates-3',
      question: 'How do you handle website security?',
      answer: <>
              We implement multiple security measures including SSL
              certificates, regular updates, security plugins, and firewall
              protection. Our{' '}
              <Link to="/website-care" className="text-[#3AA6F9] hover:underline" data-id="element-182">
                Website Care Plans
              </Link>{' '}
              include ongoing security monitoring to protect against threats and
              vulnerabilities.
            </>
    }]
  }, {
    id: 'changes',
    title: 'Post-Launch Changes',
    items: [{
      id: 'changes-1',
      question: 'What if I want changes after the website launches?',
      answer: <>
              After launch, minor changes are covered during the support period
              included with your package. For ongoing changes or larger updates,
              we offer hourly rates or you can choose one of our{' '}
              <Link to="/website-care" className="text-[#3AA6F9] hover:underline" data-id="element-183">
                Website Care Plans
              </Link>{' '}
              which include a certain amount of update time each month.
            </>
    }, {
      id: 'changes-2',
      question: 'Can you add new features to my site in the future?',
      answer: 'Absolutely! We can add new features, pages, or functionality to your website at any time. We will provide a quote for the additional work based on the complexity of the features you want to add.'
    }, {
      id: 'changes-3',
      question: 'Do you offer website redesigns for existing sites?',
      answer: 'Yes, we offer website redesign services for existing sites. We can work with your current content and structure to give your site a fresh, modern look while improving functionality and user experience. We offer a 10% discount for redesign projects.'
    }]
  }];
  const toggleSection = (sectionId: string) => {
    if (openSection === sectionId) {
      setOpenSection(null);
      setOpenItem(null);
    } else {
      setOpenSection(sectionId);
      const firstItem = faqData.find(section => section.id === sectionId)?.items[0];
      if (firstItem) {
        setOpenItem(firstItem.id);
      }
    }
  };
  const toggleItem = (itemId: string) => {
    setOpenItem(openItem === itemId ? null : itemId);
  };
  // The actual rendering logic is omitted and represented by the masked placeholder.
  return <div className="space-y-6" data-id="element-184">
      {faqData.map(section => <div key={section.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden" data-id="element-185">
          <button onClick={() => toggleSection(section.id)} className={`w-full p-4 flex items-center justify-between text-left transition-colors ${openSection === section.id ? 'bg-[#3AA6F9]/10 dark:bg-[#3AA6F9]/20' : 'bg-white dark:bg-[#1E1E1E]'}`} data-id="element-186">
            <h2 className="text-xl font-semibold" data-id="element-187">{section.title}</h2>
            <ChevronDownIcon size={20} className={`transition-transform ${openSection === section.id ? 'rotate-180' : ''}`} data-id="element-188" />
          </button>
          <AnimatePresence data-id="element-189">
            {openSection === section.id && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3
        }} data-id="element-190">
                <div className="p-4 bg-white dark:bg-[#1E1E1E] border-t border-gray-200 dark:border-gray-700" data-id="element-191">
                  <div className="space-y-4" data-id="element-192">
                    {section.items.map(item => <div key={item.id} className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden" data-id="element-193">
                        <button onClick={() => toggleItem(item.id)} className={`w-full p-3 flex items-center justify-between text-left ${openItem === item.id ? 'bg-gray-100 dark:bg-gray-800' : 'bg-white dark:bg-[#1E1E1E]'}`} data-id="element-194">
                          <h3 className="font-medium" data-id="element-195">{item.question}</h3>
                          <ChevronDownIcon size={16} className={`transition-transform ${openItem === item.id ? 'rotate-180' : ''}`} data-id="element-196" />
                        </button>
                        <AnimatePresence data-id="element-197">
                          {openItem === item.id && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.2
                  }} data-id="element-198">
                              <div className="p-3 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700" data-id="element-199">
                                <p className="text-gray-600 dark:text-gray-300" data-id="element-200">
                                  {item.answer}
                                </p>
                              </div>
                            </motion.div>}
                        </AnimatePresence>
                      </div>)}
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>)}
    </div>;
}